import { Box, Skeleton } from '@mui/material';
import { useState } from 'react';
import { KitButton, KitList } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import {
    KitBanner,
    KitChip,
    KitChips,
    KitEmpty,
    KitFab,
    KitField,
    KitSearch,
    KitSelect,
    KitStickyToolbar,
    KitThumb,
    KitToolbarButton,
} from '../common/MobileKitExtras';
import { useAppBarAction } from '../layout/AppBarActions';
import { APP_BAR_OFFSET } from '../layout/mobileShell';

/**
 * Lista de produtos no shell mobile — `prototipos-mobile/produtos/lista.html`.
 * Só apresentação: busca, filtros, paginação, permissões e ações vêm de `pages/products/List.js`.
 */

const SlidersIcon = protoIcon('sliders');
const PlusIcon = protoIcon('plus');
const PackageIcon = protoIcon('package');
const ChevronRight = protoIcon('right');
const DothIcon = protoIcon('doth');
const InfoIcon = protoIcon('info');

const STOCK_CHIPS = [
    { value: 'all', label: 'Todos' },
    { value: 'with', label: 'Com estoque' },
    { value: 'without', label: 'Sem estoque' },
];

// `.item` com `.thumb-sq` — nome, "Cód. …", preço e estoque (`.stock-low` quando zerado)
function ProductRow({ product, onOpen }) {
    const empty = Number(product.quantity) <= 0;
    return (
        <Box
            component="a"
            role="link"
            tabIndex={0}
            onClick={() => onOpen(product)}
            onKeyDown={(e) => { if (e.key === 'Enter') onOpen(product); }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                p: '13px 14px',
                minHeight: 60,
                boxSizing: 'border-box',
                borderBottom: '1px solid',
                borderColor: 'divider',
                color: 'text.primary',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:last-child': { borderBottom: 0 },
                '&:active': { backgroundColor: 'background.subtle' },
            }}
        >
            <KitThumb icon={PackageIcon} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{product.name || '—'}</Box>
                <Box sx={{ fontSize: 12, color: 'text.disabled', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: '1px', lineHeight: 1.4 }}>Cód. {product.code}</Box>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>R$ {product.price}</Box>
                <Box sx={(theme) => ({ fontSize: 11.5, lineHeight: 1.4, ...(empty ? { color: '#b45309', fontWeight: 700 } : { color: theme.palette.text.disabled }) })}>
                    {product.quantity} un
                </Box>
            </Box>
        </Box>
    );
}

// `.action-list .item` dentro da folha
function ActionItem({ icon: Icon, label, onClick, destructive, chevron = true }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                p: '12px 4px',
                minHeight: 60,
                width: '100%',
                boxSizing: 'border-box',
                border: 0,
                background: 'none',
                textAlign: 'left',
                fontFamily: 'inherit',
                color: 'text.primary',
                cursor: 'pointer',
                '&:active': { backgroundColor: 'background.subtle' },
            }}
        >
            <Box
                sx={(theme) => ({
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    backgroundColor: destructive ? theme.palette.error.bg : theme.palette.info.bg,
                    color: destructive ? theme.palette.error.main : theme.palette.primary.main,
                })}
            >
                <Icon size={20} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14, lineHeight: 1.4, color: destructive ? 'error.main' : 'text.primary' }}>{label}</Box>
            {chevron && <Box sx={{ color: 'text.disabled', display: 'inline-flex', flexShrink: 0 }}><ChevronRight size={18} /></Box>}
        </Box>
    );
}

export default function MobileProductsList({
    products,
    loading,
    totalCount,
    hasMore,
    onLoadMore,
    stockMode,
    onStockModeChange,
    search,
    onSearchChange,
    categories,
    subcategories,
    category,
    subcategory,
    onCategoryDraftChange,
    onApplyFilters,
    onClearFilters,
    canCreate,
    onNew,
    moreItems,
    rowActions,
    pickMode,
    onPick,
    onCancelPick,
}) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [draftCategory, setDraftCategory] = useState(category);
    const [draftSubcategory, setDraftSubcategory] = useState(subcategory);

    useAppBarAction({
        enabled: moreItems.length > 0,
        label: 'Mais',
        icon: DothIcon,
        onClick: () => setMoreOpen(true),
    });

    const openProduct = (product) => {
        if (pickMode) {
            onPick(product);
            return;
        }
        setSelected(product);
    };

    const actions = selected ? rowActions(selected) : [];

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitStickyToolbar top={APP_BAR_OFFSET}>
                <KitSearch
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Nome ou código de barras…"
                    inputProps={{ 'aria-label': 'Buscar produto', inputMode: 'search', enterKeyHint: 'search' }}
                />
                <KitToolbarButton
                    aria-label="Filtros"
                    onClick={() => {
                        setDraftCategory(category);
                        setDraftSubcategory(subcategory);
                        onCategoryDraftChange(category);
                        setFiltersOpen(true);
                    }}
                >
                    <SlidersIcon size={20} />
                </KitToolbarButton>
            </KitStickyToolbar>

            {pickMode && (
                <Box sx={{ mb: '12px' }}>
                    <KitBanner tone="info" title="Repor estoque" icon={InfoIcon} iconSize={18}>
                        Toque em um produto para repor o estoque.{' '}
                        <Box component="button" type="button" onClick={onCancelPick} sx={{ border: 0, background: 'none', p: 0, color: 'inherit', fontWeight: 700, fontFamily: 'inherit', fontSize: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>
                            Cancelar
                        </Box>
                    </KitBanner>
                </Box>
            )}

            <KitChips>
                {STOCK_CHIPS.map((chip) => (
                    <KitChip
                        key={chip.value}
                        active={stockMode === chip.value}
                        count={stockMode === chip.value && !loading ? totalCount : undefined}
                        onClick={() => onStockModeChange(chip.value)}
                    >
                        {chip.label}
                    </KitChip>
                ))}
            </KitChips>

            <Box sx={{ mt: '12px' }} />

            {loading && products.length === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[1, 2, 3, 4].map((n) => <Skeleton key={n} variant="rounded" height={60} sx={{ borderRadius: '16px' }} />)}
                </Box>
            )}

            {!loading && products.length === 0 && (
                <KitEmpty icon={PackageIcon} title="Nenhum produto encontrado">
                    Ajuste a busca ou os filtros para ver outros produtos.
                </KitEmpty>
            )}

            {products.length > 0 && (
                <KitList>
                    {products.map((product) => (
                        <ProductRow key={product.id} product={product} onOpen={openProduct} />
                    ))}
                </KitList>
            )}

            {hasMore && (
                <KitButton block sx={{ mt: '14px' }} onClick={onLoadMore} disabled={loading}>
                    {loading ? 'Carregando…' : 'Carregar mais'}
                </KitButton>
            )}

            {canCreate && <KitFab label="Novo" icon={PlusIcon} onClick={onNew} />}

            <MobileSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                title="Filtros"
                footer={(
                    <>
                        <KitButton
                            sx={{ flex: 1 }}
                            onClick={() => {
                                setFiltersOpen(false);
                                onClearFilters();
                            }}
                        >
                            Limpar
                        </KitButton>
                        <KitButton
                            variant="primary"
                            sx={{ flex: 1 }}
                            onClick={() => {
                                setFiltersOpen(false);
                                onApplyFilters(draftCategory, draftSubcategory);
                            }}
                        >
                            Aplicar
                        </KitButton>
                    </>
                )}
            >
                <KitField label="Categoria">
                    <KitSelect
                        value={draftCategory}
                        onChange={(e) => {
                            setDraftCategory(e.target.value);
                            setDraftSubcategory('');
                            onCategoryDraftChange(e.target.value);
                        }}
                        aria-label="Categoria"
                    >
                        <option value="">Todas</option>
                        {categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </KitSelect>
                </KitField>
                <KitField label="Subcategoria">
                    <KitSelect
                        value={draftSubcategory}
                        onChange={(e) => setDraftSubcategory(e.target.value)}
                        disabled={!draftCategory}
                        aria-label="Subcategoria"
                    >
                        <option value="">{draftCategory ? 'Todas' : 'Selecione uma categoria'}</option>
                        {subcategories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </KitSelect>
                </KitField>
            </MobileSheet>

            <MobileSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Mais">
                {moreItems.map((item) => (
                    <ActionItem
                        key={item.label}
                        icon={item.icon}
                        label={item.label}
                        onClick={() => {
                            setMoreOpen(false);
                            item.onClick();
                        }}
                    />
                ))}
            </MobileSheet>

            <MobileSheet open={Boolean(selected)} onClose={() => setSelected(null)} title={selected?.name || 'Produto'}>
                {actions.map((item) => (
                    <ActionItem
                        key={item.label}
                        icon={item.icon}
                        label={item.label}
                        destructive={item.destructive}
                        chevron={!item.destructive}
                        onClick={() => {
                            const product = selected;
                            setSelected(null);
                            item.onClick(product);
                        }}
                    />
                ))}
            </MobileSheet>
        </Box>
    );
}
